/**
 * Detailed simulations panel for the per-app stats page.
 *
 * Generic by shape — any app that returns `simulations` from
 * /api/apps/:id/stats can render this block. Today einbuergerungstest is
 * the only one. Combines:
 *   - Four metric tiles (Taken / Passed / Pass rate / Best score).
 *   - The 10-sim sparkline (chronological).
 *   - A history list (date · preset · bundesland · score · pass/fail badge).
 */
import { Check, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import type { SimulationStatsSummary } from '@/core/types';
import { useSimulationHistory } from '@/lib/queries';
import { cn } from '@/lib/utils';

type Props = {
  sims: SimulationStatsSummary;
  /** When true, fetch + render the full history list. Set false on AppHome. */
  showHistory?: boolean;
};

export function SimulationsSection({ sims, showHistory = false }: Props) {
  return (
    <section className="flex flex-col gap-4">
      <header className="flex items-baseline justify-between">
        <h2 className="font-display text-lg font-semibold">Simulations</h2>
        <span className="text-muted-foreground text-xs">{sims.total_taken} taken</span>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <MetricTile label="Taken" value={sims.total_taken} />
        <MetricTile label="Passed" value={sims.total_passed} />
        <MetricTile label="Pass rate" value={`${Math.round(sims.pass_rate * 100)}%`} />
        <MetricTile
          label="Best score"
          value={sims.best_total > 0 ? `${sims.best_correct}/${sims.best_total}` : '—'}
        />
      </div>

      {sims.trend.length > 0 ? (
        <Card>
          <CardContent className="p-4">
            <header className="mb-3 flex items-baseline justify-between">
              <h3 className="text-sm font-medium">Score trend</h3>
              <span className="text-muted-foreground text-xs">
                avg {sims.average_correct.toFixed(1)} correct
              </span>
            </header>
            <Sparkline trend={sims.trend} />
          </CardContent>
        </Card>
      ) : null}

      {showHistory ? <HistoryList /> : null}
    </section>
  );
}

function MetricTile({ label, value }: { label: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-1 p-4 pt-4">
        <div className="font-display text-2xl font-bold tabular-nums">{value}</div>
        <div className="text-muted-foreground text-xs">{label}</div>
      </CardContent>
    </Card>
  );
}

function Sparkline({ trend }: { trend: SimulationStatsSummary['trend'] }) {
  const W = 480;
  const H = 60;
  const max = Math.max(...trend.map((t) => t.total));
  const stepX = trend.length > 1 ? W / (trend.length - 1) : 0;
  const points = trend
    .map((t, i) => {
      const x = i * stepX;
      const y = H - (t.correct / max) * (H - 6) - 3;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg
      width="100%"
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="w-full"
    >
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        className="text-primary"
        vectorEffect="non-scaling-stroke"
      />
      {trend.map((t, i) => (
        <circle
          key={t.sim_id}
          cx={i * stepX}
          cy={H - (t.correct / max) * (H - 6) - 3}
          r={4}
          className={t.passed ? 'fill-primary' : 'fill-destructive'}
        />
      ))}
    </svg>
  );
}

// -----------------------------------------------------------------------------
// History list
// -----------------------------------------------------------------------------

function HistoryList() {
  const historyQuery = useSimulationHistory();

  if (historyQuery.isLoading) {
    return <p className="text-muted-foreground text-sm">Loading history…</p>;
  }
  if (historyQuery.error) {
    return (
      <p className="text-destructive text-sm">
        Couldn't load history: {historyQuery.error.message}
      </p>
    );
  }
  const items = historyQuery.data?.history ?? [];
  if (items.length === 0) {
    return (
      <p className="border-border text-muted-foreground rounded-lg border border-dashed p-4 text-center text-sm">
        No simulations yet.
      </p>
    );
  }

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium">History</h3>
      <ul className="flex flex-col gap-1.5">
        {items.map((sim) => {
          const accuracyPct = sim.total_questions
            ? Math.round((sim.correct_count / sim.total_questions) * 100)
            : 0;
          return (
            <li
              key={sim.sim_id}
              className="border-border bg-card flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 text-sm"
            >
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    'flex h-7 w-7 items-center justify-center rounded-md',
                    sim.passed
                      ? 'bg-success/20 text-success'
                      : 'bg-destructive/20 text-destructive',
                  )}
                >
                  {sim.passed ? <Check className="size-4" /> : <X className="size-4" />}
                </div>
                <div>
                  <p className="font-medium tabular-nums">
                    {sim.correct_count}/{sim.total_questions} · {accuracyPct}%
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {new Date(sim.finished_at.replace(' ', 'T') + 'Z').toLocaleString()}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge variant="outline" className="text-[10px] uppercase">
                  {sim.preset}
                </Badge>
                {sim.bundesland ? (
                  <Badge variant="muted" className="text-[10px]">
                    {sim.bundesland}
                  </Badge>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
